import { useSortable } from '@dnd-kit/sortable';
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import useTranslations from '@/i18n/useTranslations';
import autoCropImage from '@/pages/artwork/artwork-header-mobile/options/download-template/components/AutoCrop';
import UserService from '@/services/user.service';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import HandleDragListIcon from '@/shared-components/icons/components/forms/HandleDragListIcon';
import Text from '@/shared-components/text/components/Text';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import './styles/SortableRow.css';

export default function SortableRow({ style, item, isActive, deleteArtwork, formState, updateFormState }) {
  const t = useTranslations('common.profile.pages.artlist');
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id: item._id });
  const artistName = getArtworkArtistDisplayName(item);

  const itemStyle = {
    ...style,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0 : 1,
  };

  const handleDelete = async () => {
    await deleteArtwork();

    // Remove the artwork from the local form state.
    const remainingArtworks = formState.artlist_artworks.filter(art => art._id !== item._id);
    updateFormState("artlist_artworks", remainingArtworks);

    // If a cover is set, check whether its source artwork is still present.
    if (formState.artlist_image) {
      const coverSourceId = formState.artlist_image.source_artwork_id;
      const coverSourceExists = remainingArtworks.some(art => art._id === coverSourceId);
      if (!coverSourceExists) {
        if (remainingArtworks.length > 0) {
          const newCoverArtwork = remainingArtworks[0];
          autoCropImage(newCoverArtwork.artwork_media.artwork_main_picture)
            .then(croppedBlob => {
              UserService.uploadImage(croppedBlob)
                .then(({ data }) => {
                  if (data && data.image_id) {
                    updateFormState("artlist_image", { 
                      image_id: data.image_id, 
                      source_artwork_id: newCoverArtwork._id 
                    });
                  } else {
                    console.warn("SortableRow: Upload response did not return a valid image_id.");
                  }
                })
                .catch(error => console.error("SortableRow: Upload error:", error));
            })
            .catch(error => console.error("SortableRow: AutoCrop error for new cover:", error));
        } else {
          updateFormState("artlist_image", null);
        }
      } else {
        console.log("SortableRow: Cover source still exists. No cover update needed.");
      }
    }
  };

  return (
    <div 
      ref={setNodeRef} 
      style={itemStyle} 
      className="element-non-selectable" 
      {...attributes} 
      {...listeners}
    >
      <div className={`sort-artlist-artworks__artwork ${isActive ? "drag-overlay" : ""}`}>
        <DeleteIcon 
          className="sort-artlist-artworks__delete-artwork-button" 
          onClick={handleDelete}/>

        <ArtworkImage 
          image={item.artwork_media.artwork_main_picture.image_id} 
          imageClassName="sort-artlist-artworks__artwork-image" 
          imageTemplateClassName="sort-artlist-artworks__artwork-image"
        />

        <div className="sort-artlist-artworks__artwork-info">
          <Text
            className="sort-artlist-artworks__artwork-title"
            paragraph>
            {item.artwork_about.artwork_title}
          </Text>

          <Text className="sort-artlist-artworks__artwork-artist" paragraph>
            {t('labels.by')} {artistName}
          </Text>
        </div>

        <HandleDragListIcon className="sort-artlist-artworks__artwork-drag-handle"/>
      </div>
    </div>
  );
}
